import { GenericLayer } from './generic-layer'
import { LayerType } from './layer-types'
import {
    NeuroglancerAnnotationUserLayer,
    NeuroglancerWatchableValueInterface,
} from '../neuroglancer-type-mappings'
import { defaultDebounceTimeSkipFirst } from '../../utils/rxjs-pipelines'
import { SHADER_CONTROL_KEY_COLOR, SHADER_CONTROL_KEY_OPACITY } from '../../utils/shaders'
import {
    hexStringToRgbArray,
    percentToValue,
    rgbArrayToHexString,
    valueToPercent,
} from '../../utils/color-utils'
import { BaseLayer } from './base-layer'
import { Store } from '@ngrx/store'
import { LayerPropertyStoreConnector } from '../../utils/layer-property-store-connector'
import {
    TrackableService,
    WatchableValue,
} from '../../../case-viewer/neuroglancer-frame/trackable.service'

export class AnnotationLayer extends BaseLayer implements GenericLayer {
    readonly type = LayerType.Annotation

    readonly opacityPercent: WatchableValue<number> | null
    readonly opacityUnselectedPercent = null
    readonly saturationPercent = null
    readonly brightness = null
    readonly contrast = null
    readonly segmentationColor = null
    readonly shaderColor: WatchableValue<string>

    constructor(
        private readonly layer: NeuroglancerAnnotationUserLayer,
        store: Store,
        trackable: TrackableService
    ) {
        const storeConnectors = new LayerPropertyStoreConnector(store)
            .addVisibility()
            .addOpacity()
            .addShaderColor()
            .build()
        super(layer.managedLayer, storeConnectors, trackable)
        this.layer = layer

        const opacityTrackable = this.getShaderControlTrackable<number>(SHADER_CONTROL_KEY_OPACITY)
        if (opacityTrackable) {
            this.opacityPercent = trackable.wrapWatchableValueWithConversion(opacityTrackable, {
                fromNeuroglancer: valueToPercent,
                toNeuroglancer: percentToValue,
            })
            if (storeConnectors.dispatchOpacity) {
                this.opacityPercent.value$
                    .pipe(defaultDebounceTimeSkipFirst())
                    .subscribe(opacity => {
                        storeConnectors.dispatchOpacity?.(this.name, percentToValue(opacity))
                    })
            }
        } else {
            // is this rechable? (Yes, but only if we delay setting the layer's source URL.)
            console.warn('annotation layer has no opacityShaderControl?! (yet?)')
            this.opacityPercent = null
        }

        const colorTrackable =
            this.getShaderControlTrackable<Float32Array>(SHADER_CONTROL_KEY_COLOR)
        if (colorTrackable) {
            this.shaderColor = trackable.wrapWatchableValueWithConversion(colorTrackable, {
                fromNeuroglancer: rgbArrayToHexString,
                toNeuroglancer: hexStringToRgbArray,
            })
            this.shaderColor.value$.pipe(defaultDebounceTimeSkipFirst()).subscribe(color => {
                storeConnectors.dispatchShaderColor?.(this.name, color)
            })
        } else {
            // is this rechable?
            console.warn('annotation layer has no colorShaderControl?! (yet?)')
            this.opacityPercent = null
        }
    }

    getShaderControlTrackable<T>(key: string): NeuroglancerWatchableValueInterface<T> | undefined {
        return this.layer.annotationDisplayState.shaderControls.value.get(key)?.trackable
    }
}
